import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  Fragment,
  useMemo,
} from "react";

import moment from "moment";
import clsx from "clsx";

import {
  QueryElementsFromURL,
  QueryFiltersFromURL,
  getQueryParam,
  showHiddenMetrics,
  showHiddenDimensions,
  environment,
  groupMetricByCategory,
  hasMetrics,
} from "./Utils/QueryBuilderUtils";

import client from "client";
import { AppContext } from "contexts/AppContextProvider";
import { useQuery, useMutation } from "@apollo/client";

import { QueryBuilderContext } from "contexts/QueryBuilderContextProvider";

import QueryDatePicker from "components/QueryBuilder/Elements/QueryDatePicker";
import { ELEMENT_TYPES } from "components/QueryBuilder/Elements/ElementTypes";
import QueryDataNew from "components/QueryBuilder/Elements/QueryDataNew";

import { META, SAVE_QUERY } from "components/QueryBuilder/QueryBuilderQueries";

import FilterSelect from "components/QueryBuilder/Elements/FilterSelect/FilterSelect";

import {
  LogicalOperatorEnumerator,
  LogicalOperatorsOptions,
  LogicalOperatorTypes,
} from "components/QueryBuilder/Elements/FilterSelect/LogicalOperatorsOptions";
import QueryFilterCreator from "components/QueryBuilder/QueryFilters/QueryFilterCreator";
import QueryFilter from "components/QueryBuilder/QueryFilter";

import QueryPredictionOptions from "./Elements/QueryPredictionOptions";
import LogicalOperatorAndOrView from "./Elements/FilterSelect/LogicalOperatorAndOrView";
import * as Sentry from "@sentry/browser";

const QueryStructure = (props) => {
  let { state, dispatch } = useContext(QueryBuilderContext);
  const { currentProject } = useContext(AppContext).state;

  const hashParams = window.location.hash.substr(1);

  const [pastedQuery, setPastedQuery] = useState(
    props.pastedQuery ? JSON.parse(props.pastedQuery) : null
  );

  // Initialize dates from pasted query first, then fall back to state defaults
  const initialDates = useMemo(() => {
    if (props.pastedQuery) {
      const parsedQuery = JSON.parse(props.pastedQuery);
      const loadedStartDate = getQueryParam(parsedQuery, "startDate");
      const loadedEndDate = getQueryParam(parsedQuery, "endDate");

      if (loadedStartDate && loadedEndDate) {
        return {
          startDate: moment(loadedStartDate).format("YYYY-MM-DD"),
          endDate: moment(loadedEndDate).format("YYYY-MM-DD"),
        };
      }
    }
    return {
      startDate: moment(state.queryOptions.startDate).format("YYYY-MM-DD"),
      endDate: moment(state.queryOptions.endDate).format("YYYY-MM-DD"),
    };
  }, [
    props.pastedQuery,
    state.queryOptions.startDate,
    state.queryOptions.endDate,
  ]);

  const [startDate, setStartDate] = useState(initialDates.startDate);
  const [endDate, setEndDate] = useState(initialDates.endDate);

  const [queryMetrics, setQueryMetrics] = useState([]);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);

  const [fields, setFields] = useState(
    pastedQuery ? QueryElementsFromURL(pastedQuery) : [{ type: "metrics" }]
  );

  const [filters, setFilters] = useState(
    pastedQuery ? QueryFiltersFromURL(pastedQuery) : []
  );

  // Set short url on each query change
  const [saveQuery, { data: saveQueryData, error: saveQueryError }] =
    useMutation(SAVE_QUERY, {
      client,
    });

  useEffect(() => {
    if (saveQueryData) {
      window.history.replaceState(null, null, "#" + saveQueryData.saveQuery);
    }

    if (saveQueryError) {
      Sentry.captureException(saveQueryError.error);
    }
  }, [saveQueryData, saveQueryError]);

  const [showAddQueryElementButton, setAddQueryElementButton] = useState(
    hashParams ? true : false
  );

  const [showNextOptionsMenu, setShowNextOptionsMenu] = useState(false);

  // Tooltip
  const refForm = useRef();
  const refNextContainer = useRef();

  const queryNextOptions = useRef();

  // Sticky bar
  const queryTextVersion = useRef();

  // Query Metrics Once
  const {
    loading: metricsLoading,
    error: metricsError,
    data: metricsData,
  } = useQuery(META, {
    variables: {
      project_id: props.projectId,
      tenantID: parseInt(localStorage.defaultTenantID),
      env: environment(),
      hiddenMetrics: showHiddenMetrics,
      hiddenDimensions: showHiddenDimensions,
      from: initialDates.startDate,
      to: initialDates.endDate,
    },
    skip: queryMetrics.length > 0,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!metricsError && !metricsLoading && queryMetrics.length === 0) {
      setQueryMetrics(metricsData.meta);

      dispatch({
        type: "SET-METRICS",
        payload: metricsData.meta,
      });

      // Update context with the initial dates
      dispatch({
        type: "SET-QUERY-OPTIONS",
        payload: {
          ...state.queryOptions,
          startDate: initialDates.startDate,
          endDate: initialDates.endDate,
        },
      });
    }
  }, [
    metricsLoading,
    metricsError,
    metricsData,
    dispatch,
    queryMetrics.length,
    initialDates,
    state.queryOptions,
  ]);

  const renderMetrics = (field, type, i) => {
    if (metricsLoading) {
      return (
        <span
          style={{ minHeight: 20 }}
          className="d-inline-block mr-3 ml-3"
          key={i}
        >
          <div className="circle-scale circle-scale--sm d-inline-block" />
        </span>
      );
    }

    const groupedMetrics = groupMetricByCategory(queryMetrics);
    if (!metricsLoading && queryMetrics.length > 0) {
      return (
        <Fragment key={i + type}>
          {i > 0 && renderPrefix(field.type)}
          {
            <span className="query-element ml-1" key={i + type + i}>
              <FilterSelect
                mode="simple"
                data={groupedMetrics}
                value={
                  field && field.friendlyName
                    ? {
                        friendlyName: field.friendlyName,
                        name: field.name,
                      }
                    : null
                }
                // loadingStatus={data.loadingStatus}
                alwaysOpen={false}
                fieldType={field.type}
                onChangeParent={handleChange}
                index={i}
                type={"fields"}
                maxWidth={300}
              />
              {i !== 0 && renderRemoveButton(i, type)}
            </span>
          }
        </Fragment>
      );
    }
  };

  /**
   * Implements the filter structure based on Druid queries and its logical operators
   * @param {*} filter
   */
  const getQueryFilter = (filter, logicalOperator, index) => {
    const logicalOperatorType = LogicalOperatorsOptions.find(
      (lo) =>
        lo.description === logicalOperator.description &&
        lo.logicalType === logicalOperator.logicalType
    );

    let currentFilter = QueryFilterCreator(logicalOperator.primitiveType)(
      filter,
      logicalOperator
    );

    // if the logical operator for the current filter has an objection... set a not
    if (logicalOperatorType) {
      // is there a different operator other than AND?
      // re-structure the query using the OR approach
      currentFilter = {
        type:
          index === 0
            ? LogicalOperatorEnumerator.And
            : logicalOperator.binaryOperator || LogicalOperatorEnumerator.And,
        fields: [currentFilter],
      };

      if (logicalOperatorType.setObjection) {
        currentFilter = {
          type: LogicalOperatorTypes.not,
          field: {
            ...currentFilter,
          },
        };
      }
    }

    return currentFilter;
  };

  /**
   * Prepares the query structure based on the filter
   * @param {*} filters
   * @returns
   */
  const buildFilterStructure = (filters) => {
    const queryFilters = [];
    filters.forEach((statement) => {
      // if it's the first statement.. just add an AND group
      if (queryFilters.length === 0) {
        queryFilters.push({
          type: "and",
          fields: [statement],
        });
      } else {
        // Get the type of the logical operator (AND/OR). Some filters has the info
        // about its type in different structure because it changes according the
        // druid filter.
        const loType = statement.field ? statement.field.type : statement.type;

        // if the LO type is and,
        if (loType === "and") {
          // find all AND groups
          const listAND = queryFilters.filter((group) => group.type === "and");
          // and get the last AND group
          listAND[listAND.length - 1].fields.push(statement);
          // in the case the LO type is an OR... adds a new group
        } else if (loType === "or") {
          queryFilters.push({
            type: "and",
            fields: [statement],
          });
        }
      }
    });
    return queryFilters;
  };

  const updateQueryTextBar = () => {
    // Get query text version (remove date picker text)
    let queryText =
      queryTextVersion.current &&
      queryTextVersion.current.innerText
        .split("SuMoTuWeThFrSa")[0]
        .replace(/(\r\n|\n|\r)/gm, " ");
    dispatch({
      type: "SET-QUERY-TEXT-VERSION",
      payload: queryText,
    });
  };

  useEffect(() => {
    let queryData = null;
    let queryStructure = null;

    // Generate original query
    function generateOriginalQuery(generateUrl) {
      // List of metrics with id and name
      let metrics = [];

      // List of dimensions with id and name
      let dimensions = [];

      // Filters
      let _filters = [];
      let query_filters = "";

      // fields
      fields.forEach((field, i) => {
        // Find unique metrics
        if (field.type === "metrics" && field.value) {
          metrics.push({ id: parseInt(field.value), name: field.name });
        }

        // Ensure metrics are unique
        metrics = metrics.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.id === item.id)
        );

        // Dimensions: Broken Out By (Dimensions)
        if (
          field.type === "dimensions" &&
          field.value &&
          field.kind === "broken_out_by"
        ) {
          dimensions.push({ id: parseInt(field.value), name: field.name });
        }
      });

      // Filters
      filters.forEach((field, i) => {
        // Dimension Items : fields (Dimensions and Dimensions Items)
        let dimensionItemsValues = [];

        if (
          field.type === "dimensions" &&
          field.value &&
          field.kind === "filter_by"
        ) {
          // Get the next dimension items field
          if (
            filters[i + 1] &&
            filters[i + 1].type === "dimensions_items" &&
            filters[i + 1].value
          ) {
            // Account for multiple filters
            if (filters[i + 1].value.length > 1) {
              filters[i + 1].value.map((item) =>
                dimensionItemsValues.push(item.name)
              );
              _filters.push(
                getQueryFilter(
                  {
                    dimension: parseInt(filters[i].value),
                    values: dimensionItemsValues,
                  },
                  {
                    ...filters[i + 1].logicalOperator,
                    primitiveType: filters[i + 1].primitiveType,
                  },
                  i
                )
              );
            } else if (filters[i + 1].value[0]) {
              _filters.push(
                getQueryFilter(
                  {
                    dimension: parseInt(filters[i].value),
                    values: [filters[i + 1].value[0].name],
                  },
                  {
                    ...filters[i + 1].logicalOperator,
                    primitiveType: filters[i + 1].primitiveType,
                  },
                  i
                )
              );
            }
          } else {
            if (
              filters[i + 1] &&
              filters[i + 1].logicalOperator &&
              filters[i + 1].logicalOperator.value
            ) {
              _filters.push(
                getQueryFilter(
                  {
                    dimension: parseInt(filters[i].value),
                    values: filters[i + 1].logicalOperator.value,
                  },
                  filters[i + 1].logicalOperator,
                  i
                )
              );
            }
          }
        }
      });

      if (_filters.length > 0) {
        query_filters = {
          type: LogicalOperatorEnumerator.Or,
          fields: buildFilterStructure(_filters),
        };
      }

      // Build intervalSpec
      const intervalSpec = {
        interval: `${moment(startDate || state.queryOptions.startDate).format(
          "YYYY-MM-DD"
        )}/${moment(endDate || state.queryOptions.endDate).format(
          "YYYY-MM-DD"
        )}`,
      };

      const urlParams = new URLSearchParams(
        new URL(window.location.href).search
      );
      const envParam = urlParams.get("env");

      const queryData = {
        metrics: metrics,
        ...(dimensions.length > 0 && { dimensions: dimensions }),
        ...(query_filters && { filter: query_filters }),

        intervalSpec: intervalSpec,
        granularity: pastedQuery
          ? getQueryParam(pastedQuery, "granularity")
          : state.queryOptions.granularity,
        context: {
          pickaxe_project_id: currentProject.projectObj.id,
          localTimezone: "America/New_York",
          ...(showHiddenMetrics ? { hiddenMetrics: showHiddenMetrics } : {}),
          ...(showHiddenDimensions
            ? { hiddenDimensions: showHiddenDimensions }
            : {}),
          ...(envParam ? { env: envParam } : {}),
        },
      };

      // Generate query structure
      const queryStructure = JSON.stringify(queryData);

      // Generate short URL if at least one metric is selected
      if (generateUrl) {
        saveQuery({
          variables: {
            tenantID: parseInt(localStorage.defaultTenantID),
            query: queryStructure,
            projectID: currentProject.projectObj.id,
            originalQuery: generateOriginalQuery(),
          },
        });
      }

      return queryStructure;
    }

    function generateQuery(generateUrl) {
      // List of single metrics
      let metrics = [];

      // Dimensions without dimension items
      let dimensions_ids = [];

      // Dimension with dimension items
      let _filters = [];
      let query_filters = "";

      // fields
      fields.forEach((field, i) => {
        // Find unique metrics
        if (field.type === "metrics" && field.value) {
          metrics.push(parseInt(field.value));
        }

        metrics = [...new Set(metrics)];

        // Dimensions: Broken Out By (Dimensions)
        if (
          field.type === "dimensions" &&
          field.value &&
          field.kind === "broken_out_by"
        ) {
          dimensions_ids.push(parseInt(field.value));
        }
      });

      // filters
      filters.forEach((field, i) => {
        // Dimension Items : fields (Dimensions and Dimensions Items)

        let dimensionItemsValues = [];

        if (
          field.type === "dimensions" &&
          field.value &&
          field.kind === "filter_by"
        ) {
          // Get the next dimension items field
          if (
            filters[i + 1] &&
            filters[i + 1].type === "dimensions_items" &&
            filters[i + 1].value
          ) {
            // Account for multiple filters
            if (filters[i + 1].value.length > 1) {
              filters[i + 1].value.map((item) =>
                dimensionItemsValues.push(item.name)
              );
              _filters.push(
                getQueryFilter(
                  {
                    dimension: parseInt(filters[i].value),
                    values: dimensionItemsValues,
                  },
                  {
                    ...filters[i + 1].logicalOperator,
                    primitiveType: filters[i + 1].primitiveType,
                  },
                  i
                )
              );
            } else if (filters[i + 1].value[0]) {
              _filters.push(
                getQueryFilter(
                  {
                    dimension: parseInt(filters[i].value),
                    values: [filters[i + 1].value[0].name],
                  },
                  {
                    ...filters[i + 1].logicalOperator,
                    primitiveType: filters[i + 1].primitiveType,
                  },
                  i
                )
              );
            }
          } // check for other LO input values
          else {
            if (
              filters[i + 1] &&
              filters[i + 1].logicalOperator &&
              filters[i + 1].logicalOperator.value
            ) {
              _filters.push(
                getQueryFilter(
                  {
                    dimension: parseInt(filters[i].value),
                    values: filters[i + 1].logicalOperator.value,
                  },
                  filters[i + 1].logicalOperator,
                  i
                )
              );
            }
          }
        }

        if (_filters.length > 0) {
          query_filters = {
            type: LogicalOperatorEnumerator.Or,
            fields: buildFilterStructure(_filters),
          };
        }
      });

      // Data Query
      queryData = {
        metrics: metrics,
        ...(dimensions_ids.length > 0 && { dimensions: dimensions_ids }),
        ...(query_filters !== "" && { filter: query_filters }),

        startDate: moment(startDate || state.queryOptions.startDate).format(
          "YYYY-MM-DD"
        ),
        endDate: moment(endDate || state.queryOptions.endDate).format(
          "YYYY-MM-DD"
        ),

        chartType: pastedQuery
          ? getQueryParam(pastedQuery, "chartType")
          : state.queryOptions.chartType,

        granularity: pastedQuery
          ? getQueryParam(pastedQuery, "granularity")
          : state.queryOptions.granularity,

        limitSpec: {
          limit: 1000,
        },
      };

      // Structure of a query
      queryStructure = JSON.stringify(
        fields
          .concat(filters)
          .concat({
            startDate: moment(startDate || state.queryOptions.startDate).format(
              "YYYY-MM-DD"
            ),
            endDate: moment(endDate || state.queryOptions.endDate).format(
              "YYYY-MM-DD"
            ),
          })
          .concat({
            chartType: pastedQuery
              ? getQueryParam(pastedQuery, "chartType")
              : state.queryOptions.chartType,
          })
          .concat({
            granularity: pastedQuery
              ? getQueryParam(pastedQuery, "granularity")
              : state.queryOptions.granularity,
          })
          .concat(
            // concat from the pasted URL
            (pastedQuery && pastedQuery.find((p) => p["savedQuery"])) ||
              // or indeed, there's no saved query in the short url
              []
          )
          .concat({
            loadPredictions: state.loadPredictions,
            predictionRange: state.predictionRange,
            viewUncertainty: state.viewUncertainty,
          })
      );

      // Generate short url if at least one metric is selected
      if (generateUrl) {
        saveQuery({
          variables: {
            tenantID: parseInt(localStorage.defaultTenantID),
            query: queryStructure,
            projectID: currentProject.projectObj.id,
            originalQuery: generateOriginalQuery(),
          },
        });
      }

      return queryStructure;
    }

    // Existing (pasted) query
    if (pastedQuery) {
      const wasLoadPredictionSet = pastedQuery.find((param) =>
        param.hasOwnProperty("loadPredictions")
      );
      if (wasLoadPredictionSet) {
        dispatch({
          type: "SET-LOAD-PREDICTIONS",
          payload: wasLoadPredictionSet.loadPredictions,
        });

        dispatch({
          type: "SET-PREDICTIONS-RANGE",
          payload: wasLoadPredictionSet.predictionRange,
        });

        dispatch({
          type: "SET-VIEW-UNCERTAINTY",
          payload: wasLoadPredictionSet.viewUncertainty,
        });
      }

      // false means don't generate new short url
      generateQuery(false);
      setPastedQuery(null);

      if (queryData && queryStructure) {
        // Druid Query
        dispatch({
          type: "SET-QUERY",
          payload: queryData,
        });

        dispatch({
          type: "SET-QUERY-STRUCTURE",
          payload: queryStructure,
        });

        dispatch({
          type: "SET-ORIGINAL-QUERY-STRUCTURE",
          payload: generateOriginalQuery(),
        });

        dispatch({
          type: "SET-QUERY-OPTIONS",
          payload: {
            ...state.queryOptions,
            granularity: queryData.granularity,
            granularityByUser:
              queryData.granularity === "all"
                ? undefined
                : queryData.granularity,
            chartType: queryData.chartType,
          },
        });
      }
    } else {
      if (
        state.queryOptions.listMetricsIds.length > 0 ||
        (state &&
          state.query &&
          state.query.metrics &&
          state.query.metrics.length > 0)
      ) {
        generateQuery(true);
      }
      if (queryData && queryStructure) {
        // Druid Query
        dispatch({
          type: "SET-QUERY",
          payload: queryData,
        });

        dispatch({
          type: "SET-QUERY-STRUCTURE",
          payload: queryStructure,
        });

        dispatch({
          type: "SET-ORIGINAL-QUERY-STRUCTURE",
          payload: generateOriginalQuery(),
        });
      }
    }
    // Dirty way to check if query is empty
    if (
      queryTextVersion.current.querySelectorAll(".query-element")[1]
        .innerText !== ""
    ) {
      setTimeout(() => updateQueryTextBar(), 1000);
    } else {
      updateQueryTextBar();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.queryOptions,
    state.predictionRange,
    state.loadPredictions,
    state.viewUncertainty,
  ]);

  useEffect(() => {
    // Close options menu
    const handleClickOutside = (e) => {
      if (
        queryNextOptions.current &&
        queryNextOptions.current.contains(e.target)
      ) {
        return;
      } else {
        setShowNextOptionsMenu(false);
      }
    };

    if (showNextOptionsMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNextOptionsMenu]);

  const setElementValues = (i, name, id, friendlyName) => {
    const values = [...fields];
    values[i].name = name;
    values[i].value = id;
    values[i].friendlyName = friendlyName;
  };

  const setFilterElementValues = (i, name, id, friendlyName, valueType) => {
    const values = [...filters];
    values[i].name = name;
    values[i].value = id;
    values[i].friendlyName = friendlyName;
    values[i].valueType = valueType;
  };

  const handleChange = (i, elements, props) => {
    if (!elements) return;

    if (props.type === "fields") {
      // Check if we are dealing with multiselect
      const values = [...fields];

      if (!Array.isArray(elements)) {
        const type = values[i].type;
        const kind = values[i].kind;
        const { name, id, friendlyName } = elements;

        let metricsIds = [];

        if (type === "metrics") {
          setElementValues(i, name, id, friendlyName);
          setFields(values);

          fields.forEach((field, i) => {
            // Find unique metrics
            if (field.type === "metrics" && field.value) {
              metricsIds.push(parseInt(field.value));
            }

            metricsIds = [...new Set(metricsIds)];
          });

          dispatch({
            type: "SET-QUERY-OPTIONS",
            payload: {
              ...state.queryOptions,
              listMetricsIds: metricsIds,
            },
          });

          setAddQueryElementButton(true);
        }

        // Dimension Items must use name not IDs
        if (type === "dimensions_items") {
          setElementValues(i, name, id, friendlyName);
          setFields(values);
          setAddQueryElementButton(true);
        }

        // Broken Out By - load only dimensions
        if (type === "dimensions" && kind === "broken_out_by") {
          setElementValues(i, name, id, friendlyName);
          setFields(values);

          dispatch({
            type: "SET-QUERY-OPTIONS",
            payload: {
              ...state.queryOptions,
              currentDimensionId: parseInt(id),
            },
          });

          setAddQueryElementButton(true);
        }

        // Multi select with one value
        if (type === "dimensions" && kind === "filter_by") {
          setElementValues(i, name, id, friendlyName);
          setFields(values);

          dispatch({
            type: "SET-QUERY-OPTIONS",
            payload: {
              ...state.queryOptions,
              currentDimensionId: parseInt(id),
            },
          });

          setAddQueryElementButton(false);
          // if we have more fields after current one
          if (values.length - 1 > i) {
            if (values[i + 1].type === "dimension_items") {
            }
          } else {
            addField("dimensions_items", "filter_by");
          }
        }
      } else {
        // Multiselect with multiple values
        const type = values[i].type;

        //Filter By Dimensions - load both dimensions and dimension items
        if (type === "dimensions_items") {
          let optionValues = [];

          elements.map((option) =>
            optionValues.push({
              friendlyName: option.friendlyName,
              name: option.name,
            })
          );

          values[i].name = "dimensions_items";
          values[i].kind = "filter_by";
          // Default options
          values[i].value = optionValues;
          // Matching dimension name
          values[i].friendlyName = values[i - 1].friendlyName;

          setFields(values);

          dispatch({
            type: "SET-QUERY-OPTIONS",
            payload: {
              ...state.queryOptions,
            },
          });

          setAddQueryElementButton(true);
        }
      }
    } else if (props.type === "filters") {
      // Check if we are dealing with multiselect
      const values = [...filters];
      if (!Array.isArray(elements)) {
        const type = values[i].type;
        const kind = values[i].kind;
        const { name, id, friendlyName, valueType } = elements;

        // Multi select with one value
        if (type === "dimensions" && kind === "filter_by") {
          setFilterElementValues(i, name, id, friendlyName, valueType);

          // if we have more fields after current one
          const nextType = "dimensions_items";
          const field = ELEMENT_TYPES.find((o) => o.type === nextType);
          const value = { type: field.type, kind: kind };

          if (values.length - 1 > i) {
            if (values[i + 1].type === nextType) {
              values.splice(i + 1, 1, value);
            } else {
              values.splice(i + 1, 0, value);
            }
          } else {
            values.push(value);
          }

          setFilters(values);

          dispatch({
            type: "SET-QUERY-OPTIONS",
            payload: {
              ...state.queryOptions,
            },
          });
        }
      } else {
        // Multiselect with multiple values
        const type = values[i].type;

        // Filter By Dimensions - load both dimensions and dimension items
        if (type === "dimensions_items") {
          let optionValues = [];
          elements.forEach((option) => {
            optionValues.push({
              friendlyName: option.friendlyName,
              name: option.name,
            });
          });

          values[i].name = "dimensions_items";
          values[i].kind = "filter_by";

          if (props.logicalOperator && props.primitiveType) {
            values[i].logicalOperator = props.logicalOperator;
            values[i].primitiveType = props.primitiveType;
          }

          // determines the binary operator by the dimension key (not dimension_items) OR the default one (AND)
          values[i].logicalOperator = {
            ...values[i].logicalOperator,
            binaryOperator:
              values[i - 1].logicalOperator.binaryOperator ||
              LogicalOperatorEnumerator.And,
          };

          // Default options
          values[i].value = optionValues;
          // Matching dimension name
          values[i].friendlyName = values[i - 1].friendlyName;

          setFilters(values);
          dispatch({
            type: "SET-QUERY-OPTIONS",
            payload: {
              ...state.queryOptions,
            },
          });
        }
      }
    }
  };

  const handleDateChange = (startDate, endDate) => {
    if (startDate && endDate) {
      setStartDate(moment(startDate).format("YYYY-MM-DD"));
      setEndDate(moment(endDate).format("YYYY-MM-DD"));

      dispatch({
        type: "SET-QUERY-OPTIONS",
        payload: {
          ...state.queryOptions,
          listMetricsIds: state.query.metrics || [],
          startDate: moment(startDate).format("YYYY-MM-DD"),
          endDate: moment(endDate).format("YYYY-MM-DD"),
        },
      });
    }
  };

  const handleNextOptions = (e, type, kind, queryType) => {
    if (queryType === "filters") {
      if (hasMetrics(state)) {
        addFilter(type, kind);
      }
    } else {
      setShowNextOptionsMenu(false);

      setAddQueryElementButton(false);

      addField(type, kind);
    }

    e.preventDefault();
  };

  const addField = (type, kind) => {
    const field = ELEMENT_TYPES.find((o) => o.type === type);
    const fieldType = field.type;

    if (kind === "filter_by") {
      dispatch({
        type: "SET-QUERY-OPTIONS",
        payload: {
          ...state.queryOptions,
        },
      });

      setShowNextOptionsMenu(false);
    }

    if (kind === "broken_out_by") {
      dispatch({
        type: "SET-QUERY-OPTIONS",
        payload: {
          ...state.queryOptions,
        },
      });

      setShowNextOptionsMenu(true);
    }

    const elements = [...fields];

    if (type === "metrics") {
      // Find the last index of metric field
      const lastMetricPosition = elements
        .map((field) => field.type)
        .lastIndexOf("metrics");

      elements.splice(lastMetricPosition + 1, 0, {
        type: fieldType,
        kind: kind,
      });
    } else {
      elements.push({ type: fieldType, kind: kind });
    }

    setFields(elements);
  };

  const addFilter = (type, kind) => {
    const field = ELEMENT_TYPES.find((o) => o.type === type);
    const fieldType = field.type;

    const elements = [...filters];
    elements.push({ type: fieldType, kind: kind });

    setFilters(elements);
  };

  const AddQueryElementButton = () => (
    <div className="next_container" ref={refNextContainer}>
      <div onClick={toggleNextOptions} className="query-add-element-btn" />

      {showNextOptionsMenu && renderNextOptions()}
    </div>
  );

  const AddFilterElementButton = () => (
    <button
      type="button"
      onClick={(e) => {
        // don't allow to add more than a filter if the latest one wasn't selected yet
        // if (!filters.find((f) => !f.name))
        handleNextOptions(e, "dimensions", "filter_by", "filters");
      }}
      className="btn btn-sm rounded query-builder-filter-button ml-2"
    >
      +<span className="font-weight-bold">Filter</span>
    </button>
  );

  /**
   * Handles the toggle to show/hide the + Prediciton button
   * @returns
   */
  const AddPredictionElementButton = () => {
    return (
      <>
        {!state.loadPredictions && (
          <button
            type="button"
            onClick={(e) =>
              dispatch({ type: "SET-LOAD-PREDICTIONS", payload: true })
            }
            className="btn btn-sm rounded query-builder-filter-button ml-2"
          >
            +<span className="font-weight-bold">Prediction</span>
          </button>
        )}
      </>
    );
  };

  const toggleNextOptions = () => {
    setShowNextOptionsMenu(showNextOptionsMenu === true ? false : true);
  };

  const removeField = (i, e) => {
    e.preventDefault();

    const values = [...fields];
    const type = values[i].type;
    const kind = values[i].kind;

    if (type === "metrics") {
      const currentElementValue = parseInt(values[i].value);
      const newMetricsSet = state.queryOptions.listMetricsIds.filter(
        (o) => o !== currentElementValue
      );

      dispatch({
        type: "SET-QUERY-OPTIONS",
        payload: {
          ...state.queryOptions,
          listMetricsIds: newMetricsSet,
        },
      });
    }

    if (type === "dimensions" && kind === "broken_out_by") {
      dispatch({
        type: "SET-QUERY-OPTIONS",
        payload: {
          ...state.queryOptions,
          currentDimensionId: null,
        },
      });
    }

    if (type === "dimensions" && kind === "filter_by") {
      dispatch({
        type: "SET-QUERY-OPTIONS",
        payload: {
          ...state.queryOptions,
          currentDimensionId: null,
        },
      });
    }

    const hasMetricsField = values.filter(
      (field) => field.type === "metrics"
    ).length;

    // Reset query if all metrics have been removed
    if (hasMetricsField === 1 && type !== "dimensions") {
      resetQuery();
    } else {
      // Remove field
      values.splice(i, 1);
      setFields(values);
    }

    if (type === "dimensions_items") {
      setAddQueryElementButton(false);
    } else {
      setAddQueryElementButton(true);
    }
  };

  const removeFilter = (i, e) => {
    // `e` may not be used
    e && e.preventDefault();

    let values = [...filters];
    const type = values[i].type;
    const kind = values[i].kind;

    if (type === "dimensions" && kind === "filter_by") {
      if (values[i + 1] && values[i + 1].type === "dimensions_items") {
        values = [...values.slice(0, i), ...values.slice(i + 2)];
      } else {
        values = [...values.slice(0, i), ...values.slice(i + 1)];
      }
    } else if (type === "dimensions_items" && kind === "filter_by") {
      values = [...values.slice(0, i - 1), ...values.slice(i + 1)];
    }

    setFilters(values);

    dispatch({
      type: "SET-QUERY-OPTIONS",
      payload: {
        ...state.queryOptions,
      },
    });
  };

  const cleanFilter = (i) => {
    //e.preventDefault();

    let values = [...filters];

    values = [...values.slice(0, i), ...values.slice(i + 1)];

    setFilters(values);

    // setQueryOpts({
    //   ...queryOpts,
    //   // canBeFiltered: false
    // });
    dispatch({
      type: "SET-QUERY-OPTIONS",
      payload: {
        ...state.queryOptions,
      },
    });
  };

  const resetQuery = () => {
    window.history.replaceState(null, null, " ");

    dispatch({
      type: "SET-QUERY-OPTIONS",
      payload: {
        ...state.queryOptions,
      },
    });
  };

  const renderRemoveButton = (i) => (
    <div className="position-relative d-inline query-remove-container">
      <button
        className="query-remove-element-btn"
        onClick={(e) => removeField(i, e)}
      />
    </div>
  );

  const renderRemoveFilterButton = (i) => (
    <div className="query-remove-container">
      <button
        className="filter-remove-element-btn"
        onClick={(e) => removeFilter(i, e)}
      />
    </div>
  );

  const renderNextOptions = () => {
    return (
      <div
        ref={queryNextOptions}
        className={clsx("query-next-options-left", {
          "query-next-options-right":
            +refForm.current.offsetWidth -
              +refNextContainer.current.offsetLeft >
            150,
        })}
      >
        <button
          className="query-next-options__button"
          onClick={(e) => handleNextOptions(e, "metrics")}
        >
          Add Another Metric
        </button>

        <button
          className="query-next-options__button"
          onClick={(e) => handleNextOptions(e, "dimensions", "broken_out_by")}
        >
          Broken out by
        </button>
      </div>
    );
  };

  const renderPrefix = (type, kind, index, nextField) => {
    let prefix = "";

    switch (type) {
      case "metrics":
        prefix = <span className="query-element">and</span>;
        break;
      case "dimensions" || "dimensions_items":
        prefix =
          kind === "filter_by" ? (
            <LogicalOperatorAndOrView
              index={index}
              onSwitchBinaryOperator={setBinaryOperator}
              value={nextField.logicalOperator?.binaryOperator}
            />
          ) : (
            <span className="query-element">broken out by</span>
          );
        break;
      /*case "dimensions_items":
        prefix = <span className="query-element">is</span>;
        break;*/
      default:
        prefix = (
          <LogicalOperatorAndOrView
            isPrefixDefault
            index={index}
            onSwitchBinaryOperator={setBinaryOperator}
            value={nextField.logicalOperator?.binaryOperator}
          />
        );
        break;
    }

    return prefix;
  };

  const renderFields = (type) => {
    return fields.map((field, i) => {
      let renderElementType = "";
      let queryVariables = {};

      if (field.type === "dimensions") {
        queryVariables = {
          ...{ metricIds: state.query.metrics },
          ...{ dimensionId: state.queryOptions.currentDimensionId },
        };
      } else if (field.type === "dimensions_items") {
        // Get dimension from corresponding field
        queryVariables = {
          ...{ metricIds: state.query.metrics },
          ...{ dimensionId: fields[i - 1].value },
        };
      }

      let queryDataProps = {
        query: ELEMENT_TYPES.find((o) => o.type === field.type).query,
        key: i,
        variables: {
          ...queryVariables,
          ...{
            project_id: props.projectId,
            tenantID: parseInt(localStorage.defaultTenantID),
          },
          env: environment(),
          hiddenMetrics: showHiddenMetrics,
          hiddenDimensions: showHiddenDimensions,
        },
      };

      let dimensionItemsValues = [];
      if (field.type === "dimensions_items" && field.value !== undefined) {
        field.value.map((option) =>
          dimensionItemsValues.push({
            friendlyName: option.friendlyName,
            name: option.name,
          })
        );
      }

      if (field.type === "metrics") {
        return renderMetrics(field, type, i);
      }

      if (field.type === "dimensions" || field.type === "dimensions_items") {
        renderElementType = (
          <Fragment key={i + type}>
            {i > 0 && renderPrefix(field.type)}
            {
              <QueryDataNew {...queryDataProps}>
                {(data) => {
                  let options = data.data ? data.data.meta : [];
                  if (field.type === "dimensions")
                    options = options
                      .filter((el) => el.isGroupable)
                      .sort((a, b) =>
                        a.friendlyName > b.friendlyName ? 1 : -1
                      );
                  return (
                    <span className="query-element">
                      <FilterSelect
                        mode="simple"
                        data={options}
                        value={
                          field && field.friendlyName
                            ? {
                                friendlyName: field.friendlyName,
                                name: field.name,
                              }
                            : null
                        }
                        loadingStatus={data.loadingStatus}
                        alwaysOpen={false}
                        fieldType={field.type}
                        onChangeParent={handleChange}
                        index={i}
                        type={type}
                        maxWidth={300}
                      />
                      {renderRemoveButton(i)}
                    </span>
                  );
                }}
              </QueryDataNew>
            }
          </Fragment>
        );
      }

      return renderElementType;
    });
  };

  /**
   * Set the logical operator in the filter according its index
   */
  const setLogicalOperator = (filterIndex, logicalOperator) => {
    filters[filterIndex] = {
      ...filters[filterIndex],
      logicalOperator,
    };
    // because we've changed the Logical Operator.. delete the value in order to be set a new value
    delete filters[filterIndex].value;

    dispatch({
      type: "SET-QUERY-OPTIONS",
      payload: {
        ...state.queryOptions,
      },
    });
  };

  /**
   * Changes the binary operator (OR, AND) for the current filter
   * @param {*} index
   * @param {*} operator
   */
  const setBinaryOperator = (index, operator) => {
    // set the binaryOperator to the current filter
    if (filters[index].logicalOperator) {
      filters[index].logicalOperator.binaryOperator = operator;
      // request data based on the changes
      dispatch({
        type: "SET-QUERY-OPTIONS",
        payload: {
          ...state.queryOptions,
        },
      });
    }
  };

  /**
   * Set the selected primitive type from Select and persist it in the URL
   * @param {*} filterIndex
   * @param {*} primitiveType
   */
  const setPrimitiveType = (filterIndex, primitiveType) => {
    filters[filterIndex] = {
      ...filters[filterIndex],
      primitiveType,
    };
    delete filters[filterIndex].value;

    dispatch({
      type: "SET-QUERY-OPTIONS",
      payload: {
        ...state.queryOptions,
      },
    });
  };

  /**
   * Persist the value set from the Filter LO into the current filter
   * @param {*} filterIndex
   * @param {*} value
   */
  const applyFilterValue = (filterIndex, value, primitiveTypeOfData) => {
    filters[filterIndex] = {
      ...filters[filterIndex],
      logicalOperator: {
        ...filters[filterIndex].logicalOperator,
        // this is the primitive type of the data and logical operator selected.. not from the current logical operator
        primitiveType: primitiveTypeOfData,
        // TODO: this `value` prop should be in the in the `filters[filterIndex]` as prop
        value,
        binaryOperator:
          filters[filterIndex - 1].logicalOperator.binaryOperator ||
          LogicalOperatorEnumerator.And,
      },
    };
    dispatch({
      type: "SET-QUERY-OPTIONS",
      payload: {
        ...state.queryOptions,
      },
    });
  };

  const onSetFilterMenuIsOpen = (isOpen) => {
    setIsFilterMenuOpen(isOpen);
  };

  const renderFilters = (type) =>
    filters.map((field, i) => {
      let renderElementType = "";
      let queryVariables = {};
      let choosedDimension = null;
      const isNextDemensionsItems =
        filters[i + 1] && filters[i + 1].type === "dimensions_items";
      const nextField = isNextDemensionsItems
        ? { ...filters[i + 1] }
        : { ...field };

      if (field.type === "dimensions") {
        choosedDimension = null;
        if (isNextDemensionsItems) {
          choosedDimension = field.friendlyName;
          queryVariables = Object.assign(
            { metricIds: state.queryOptions.listMetricsIds },
            { dimensionId: field.value },
            { canBeFiltered: state.queryOptions.canBeFiltered },
            { canBeGroupped: state.queryOptions.canBeGroupped }
          );
        } else {
          queryVariables = Object.assign(
            { metricIds: state.queryOptions.listMetricsIds },
            { dimensionId: state.queryOptions.currentDimensionId },
            { canBeFiltered: state.queryOptions.canBeFiltered },
            { canBeGroupped: state.queryOptions.canBeGroupped }
          );
        }
      } else if (field.type === "dimensions_items") {
        return "";
      }

      let queryDataProps = {
        query: ELEMENT_TYPES.find((o) => o.type === nextField.type).query,
        key: i,
        variables: {
          ...queryVariables,
          ...{
            project_id: props.projectId,
            tenantID: localStorage.defaultTenantID,
          },
          env: environment(),
          hiddenMetrics: showHiddenMetrics,
          hiddenDimensions: showHiddenDimensions,
        },
      };

      let dimensionItemsValues = [];
      if (
        nextField.type === "dimensions_items" &&
        nextField.value !== undefined
      ) {
        nextField.value.map((option) =>
          dimensionItemsValues.push({
            friendlyName: option.friendlyName,
            name: option.name,
          })
        );
      }

      if (
        nextField.type === "dimensions" ||
        nextField.type === "dimensions_items"
      ) {
        renderElementType = (
          <Fragment key={i}>
            {i > 0 ? (
              renderPrefix(
                nextField.type,
                nextField.kind,
                isNextDemensionsItems ? i + 1 : i,
                nextField
              )
            ) : (
              <div className="query-element mb-3">
                <span className="mr-2 ml-1" key={i + "_title"}>
                  <span className="text-info">Filter </span>
                  <span>by</span>
                </span>
              </div>
            )}

            {
              <QueryFilter
                queryDataProps={queryDataProps}
                filterSelectProps={{
                  field,
                  nextField,
                  fieldType: nextField.type,
                  onChangeParent: handleChange,
                  index: isNextDemensionsItems ? i + 1 : i,
                  type,
                  choosedDimension,
                  cleanFilter: cleanFilter,
                  choosedValue:
                    dimensionItemsValues.length > 0 ? dimensionItemsValues : [],
                  tabNames: ["dimension"],
                  maxWidth: 300,
                  alwaysOpen: false,
                  onLogicalOperatorChange: setLogicalOperator,
                  onSelectPrimitiveType: setPrimitiveType,
                  onApplyFilter: applyFilterValue,
                  defaultLogicalOperator: nextField,
                  onSetIsOpen: onSetFilterMenuIsOpen,
                  isFilterMenuOpen,
                  renderRemoveFilterButton: renderRemoveFilterButton,
                  removeFilter: removeFilter,
                  filterIndex: i,
                }}
              />
            }
          </Fragment>
        );
      }

      return renderElementType;
    });

  const renderElements = (type) =>
    type === "fields" ? renderFields(type) : renderFilters(type);

  const metricsApplied = useMemo(
    () => fields.filter((filter) => filter.value),
    [fields]
  );

  const canDisplayPredictionOptions =
    metricsApplied.length > 0 &&
    // chart types which don't support predictions now
    ["table", "pivot-table", "bar-chart-stacked"].indexOf(
      state.queryOptions.chartType
    ) === -1;

  // const CopyNativeQuery = () => {
  //   const [statusMessage, setStatusMessage] = useState("");
  //   const copyTextToClipboard = async (event) => {
  //     event.preventDefault();

  //     try {
  //       await navigator.clipboard.writeText(
  //         JSON.stringify({ query: state.query })
  //       );
  //       setStatusMessage("Text copied to clipboard!");
  //       setTimeout(() => setStatusMessage(""), 1200);
  //     } catch (err) {
  //       setStatusMessage("Failed to copy. Please try again.");
  //       setTimeout(() => setStatusMessage(""), 1200);
  //       console.error("Failed to copy: ", err);
  //     }
  //   };

  //   return (
  //     <button
  //       className="btn btn-sm rounded query-builder-filter-button ml-2"
  //       onClick={copyTextToClipboard}
  //     >
  //       <span className="font-weight-bold">
  //         {statusMessage ? statusMessage : "Copy Widget Query"}
  //       </span>
  //     </button>
  //   );
  // };

  return (
    <form ref={refForm}>
      <div className="query-container" ref={queryTextVersion}>
        <span className="query-element ml-1">Show me </span>
        {renderElements("fields")}
        <span className="query-element ml-1">for</span>
        <span className="query-element">
          <QueryDatePicker
            dateRanges={props.dateRanges}
            onChange={handleDateChange}
            selectedDate={startDate}
            startDate={startDate}
            endDate={endDate}
          />
        </span>
        {showAddQueryElementButton && AddQueryElementButton()}
        <span className="query-element query-element__common"></span>
      </div>
      <div className="query-container mt-3 d-flex flex-wrap flex-row">
        {renderElements("filters")}
        {canDisplayPredictionOptions && (
          <QueryPredictionOptions filters={filters} />
        )}
        {AddFilterElementButton()}
        {canDisplayPredictionOptions && AddPredictionElementButton()}
        {/* {CopyNativeQuery()} */}
      </div>
    </form>
  );
};

export default QueryStructure;
